import React, {
  useEffect,
  useState,
} from 'react';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import {
  Field,
  Form,
} from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import {
  pick,
  times,
} from 'lodash';
import styled from 'styled-components';
import {
  Backdrop,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Typography,
} from '@material-ui/core';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';

import { AddressAutocomplete } from '@/core/components/AddressAutocomplete';
import { Popover } from '@/core/components/Popover';
import { ShareIcons } from '@/core/components/ShareIcons';
import {
  ActionCenterTypes,
  AutoCompleteAddresses,
  DataSuggestions,
  MessageElementStyled,
  MessageForm,
  Nullable,
  SubjectMessagePair,
  TakeActionLandingPageProps,
  Targets,
  TextElementStyled,
} from '@/core/interfaces/common';

import {
  CustomFields,
  EmailField,
  FirstNameField,
  IsOptEmailField,
  IsOptTextField,
  LastNameField,
  MessageField,
  PersonalizedMessageField,
  PhoneNumberField,
  PrefixField,
  StreetAddressField,
  SubjectField,
  ZipCodeField,
} from '@/features/Form';

import { request } from '@/utils/request';
import mediaQueries from '@/utils/mediaQueries';
import { required } from '@/utils/validators';
import { colors } from '@/utils/constants';
import { googleAnalyticsPageview } from '@/utils/analytics';
import {
  handleNumberFormat,
  replaceStringLineBreaksWithHtml,
} from '@/utils/helpers';

import {
  CircularProgressWrapper,
  SectionTitle,
  SendMessageButton,
  StyledTextField as StyledTextFieldBase,
} from '@/themes/globalStyles';

const {
  small,
  xsmall,
} = mediaQueries;

const LogoWrapper = styled.div`
  padding: 0 20px;
`;

const Logo = styled.img`
  max-height: 50px;
`;

const LandingPageWrapper = styled(Grid)`
  width: 100%;
  max-width: 1452px;
  margin: 0 auto;
`;

const Container = styled(Grid)`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
`;

const StyledContainer = styled(Container)<MessageElementStyled>`
  padding-top: 80px;
  padding-bottom: ${props => (props.messagesent ? '0' : '120px')};
`;

const HeadlineSectionWrapper = styled(Grid)<MessageElementStyled>(props => (
  props.messagesent ?
    {
      backgroundColor: props.backgroundcolor || '#2979FF',
      padding: '0 20px',
      width: '100%',
    } :
    {
      backgroundImage: `linear-gradient(172deg, ${props.backgroundcolor || '#2979FF'} 70%, ${colors.white} calc(70% + 2px))`,
      padding: '0 20px',
      width: '100%',
    }
));

const BaseSection = styled.div`
  background-color: ${colors.wildSand};
`;

const ActionCenterText = styled(Typography)<TextElementStyled>`
  color: ${props => (props.textcolor || colors.tableHeaderGray)};
  font-weight: bold;
  font-size: 12px;
  line-height: 15px;
`;

const DescriptionSection = styled.section<MessageElementStyled>`
  margin-top: ${props => (props.messagesent ? '50px' : '0')};
  padding: 0 95px 50px;

  @media ${xsmall} {
    padding: 0 25px 50px;
  }
`;

const MatchTargetsHeaderSection = styled.section`
  padding: 46px 95px 54px;
  background-color: ${colors.dodgerBlue};

  @media ${xsmall} {
    padding: 46px 25px 54px;
  }
`;

const MessageSentSectionWrapper = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 100px 0 80px;
  padding: 46px 95px 38px;
  background-color: ${colors.wildSand};

  @media ${xsmall} {
    margin: 50px 0 40px;
    padding: 46px 25px 38px;
  }
`;

const MatchTargetsSection = styled.section`
  margin-bottom: 4px;
  padding: 34px 95px 38px;
  background-color: ${colors.dodgerBlue};
  color: ${colors.white};

  @media ${xsmall} {
    padding: 34px 25px 28px;
  }
`;

const FormFieldWrapper = styled(BaseSection)`
  margin-bottom: 4px;
  padding: 46px 95px 38px;

  @media ${xsmall} {
    padding: 46px 25px 38px;
  }
`;

const SendMessageButtonWrapper = styled(BaseSection)`
  display: flex;
  justify-content: center;
  margin-bottom: 4px;
  padding: 46px 95px 38px;
`;

const DisclaimerSection = styled(BaseSection)`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
  padding: 26px 95px 28px;

  @media ${xsmall} {
    padding: 26px 25px 28px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  box-shadow: 0 2px 4px 0 rgb(0 0 0 / 13%);
  border: 18px solid ${colors.white};
  object-fit: cover;
`;

const Headline = styled.h1<TextElementStyled>`
  width: 100%;
  margin: 32px 0;
  padding-right: 40px;
  color: ${props => (props.textcolor || colors.white)};
  font-weight: bold;
  font-size: 48px;
  line-height: 60px;

  @media ${xsmall} {
    padding-right: 0;
  }
`;

const HeadlineDescription = styled.p<TextElementStyled>`
  width: 100%;
  padding-right: 115px;
  color: ${props => (props.textcolor || colors.tableHeaderGray)};
  font-size: 16px;
  line-height: 24px;

  @media ${xsmall} {
    padding-right: 0;
    padding-bottom: 20px;
  }
`;

const StyledText = styled.p<TextElementStyled>`
  width: 100%;
  margin: 16px 0;
  color: ${props => (props.textcolor || colors.tableHeaderGray)};
  font-size: 16px;
  line-height: 24px;
`;

const StyledTextCenter = styled(StyledText)`
  text-align: center;
`;

const SectionTitleCenter = styled(SectionTitle)`
  text-align: center;
`;

const MainImageWrapper = styled.div<MessageElementStyled>`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 0;
  margin-bottom: ${props => (props.messagesent ? '-47px' : '0')};
  padding-top: 35px;
  padding-bottom: 56.25%;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const FormTitle = styled(Typography)`
  display: flex;
  justify-content: space-between;
  border-left: 4px solid ${colors.dodgerBlue};
  padding-left: 7px;
  font-size: 16px;
  line-height: 20px;
`;

const TakeActionTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledTextField = styled(StyledTextFieldBase)`
  padding-right: 30px;
`;

const StyledMatchTargetsTextField = styled(StyledTextField)`
  /* stylelint-disable-next-line selector-class-pattern */
  .MuiInput-underline-154::before {
    border-color: white;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .MuiInput-underline-154:hover:not(.Mui-disabled)::before {
    border-color: white;
  }

  @media ${small} {
    margin-bottom: 20px;
  }
`;

const ClearButton = styled(Button)`
  height: 40px;
  color: ${colors.gold};
  font-weight: bold;
`;

const MatchTargetsButton = styled(Button)`
  width: 170px;
  height: 40px;
  background-color: ${colors.gold};
  font-weight: bold;
`;

const DividingLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgb(255 255 255 / 25%);
`;

const MatchTargetsResultsTitle = styled.h3`
  color: ${colors.white};
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
`;

const MatchTargetItem = styled.div`
  padding: 13px 30px 11px;
  background-color: ${colors.white};
  color: ${colors.tableHeaderGray};
`;

const MatchTargetItemName = styled.h5`
  margin: 0;
  font-weight: bold;
  font-size: 16px;
`;

const MatchTargetItemPosition = styled.p`
  margin: 0;
  color: rgb(64 82 93 / 54%);
  font-weight: bold;
  font-size: 16px;
`;

const NoData = styled.div`
  margin: 20px 8px;
  color: inherit;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 55px;
`;

const MessageBlock = styled(Typography)`
  margin-top: 30px;
  padding-right: 30px;
  white-space: pre-wrap;
`;

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SubTitle = styled.p`
  width: 310px;
  margin: 16px 0;
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
`;

const StyledCheckCircleRoundedIcon = styled(CheckCircleRoundedIcon)`
  position: absolute;
  top: -48px;
  left: 50%;
  width: 96px;
  height: 96px;
  color: green;
  transform: translate(-50%);
`;

const PaginationSign = styled.div<{ isCurrentlySelected: boolean }>`
  position: relative;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${colors.gold};

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    display: ${props => (props.isCurrentlySelected ? 'block' : 'none')};
    width: 11px;
    height: 11px;
    border: 1px solid ${colors.gold};
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
`;

const DonateButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const DonateButton = styled.a`
  text-decoration: none;
`;

const ClearFormButton = styled(Button)`
  padding: 0;
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: 1000;
`;

const BrandingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 5px;
  padding-top: 10px;
`;

const LogoMark = styled.img`
  max-height: 30px;
  margin: 5px 0;
`;

const StyledGrid = styled(Grid)`
  padding-right: 30px;
`;

const { REACT_APP_SMARTY_STREETS_API_KEY } = getConfig().publicRuntimeConfig;

export const TakeActionLandingPageV1 = ({
  actionCenterDetails,
  contactInfo,
  nonGeocodedTargets,
}: TakeActionLandingPageProps) => {
  const router = useRouter();
  const { uuid } = router.query;

  const {
    customFields,
    facebookPixelEnabled,
    facebookPixelId,
    headlineConfig,
    isDonateButton,
    isGeocoded,
    googleAnalyticsEnabled,
    googleAnalyticsId,
    landingPageImage,
    linkDonate,
    logo,
    organization,
    prefixes,
    status,
    socialShareButtonEnabled,
    thankYouMessage,
  } = actionCenterDetails;
  const youtubeId = headlineConfig?.youtubeId;

  const [
    paginationPage,
    setPaginationPage,
  ] = useState(0);
  const [
    addressSuggestions,
    setAddressSuggestions,
  ] = useState<AutoCompleteAddresses>([]);
  const [
    matchedTargets,
    setMatchedTargets,
  ] = useState<Targets>([]);
  const [
    isMatchedTargetsFetching,
    setIsMatchedTargetsFetching,
  ] = useState(false);
  const [
    messageSent,
    setMessageSent,
  ] = useState(false);
  const [
    showEditPreferences,
    setShowEditPreferences,
  ] = useState(false);
  const [
    isMatchedTargetsRequestRun,
    setIsMatchedTargetsRequestRun,
  ] = useState(false);
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    isAutocompletePending,
    setIsAutocompletePending,
  ] = useState(false);
  const [
    prefilledMessage,
    setPrefilledMessage,
  ] = useState<Nullable<SubjectMessagePair>>(null);
  const [
    isNewContact,
    setIsNewContact,
  ] = useState(false);

  useEffect(() => {
    if (!isGeocoded && nonGeocodedTargets?.length) {
      setMatchedTargets(nonGeocodedTargets);
    }
  }, [
    nonGeocodedTargets,
    isGeocoded,
  ]);

  useEffect(() => {
    setPrefilledMessage(() => (actionCenterDetails?.prefilledMessages ?
      actionCenterDetails.prefilledMessages[Math.floor(
        Math.random() * actionCenterDetails.prefilledMessages.length
      )] :
      null));
  }, [actionCenterDetails]);

  useEffect(() => {
    if (facebookPixelId && facebookPixelEnabled) {
      import('react-facebook-pixel')
        .then(module => module.default)
        .then(ReactPixel => {
          ReactPixel.init(facebookPixelId, undefined, {
            autoConfig: true,
            debug: false,
          });
          ReactPixel.pageView(); // For tracking page view
        });
    }
  }, [
    facebookPixelId,
    facebookPixelEnabled,
  ]);

  useEffect(() => {
    if (googleAnalyticsId && googleAnalyticsEnabled) {
      googleAnalyticsPageview(googleAnalyticsId);
    }
  }, [
    googleAnalyticsId,
    googleAnalyticsEnabled,
  ]);

  const handleClearAddress = (
    change: <F extends keyof MessageForm>(name: F, value?: MessageForm[F] | undefined) => void
  ) => {
    change('streetAddress', undefined);
    change('zipCode', '');
    setMatchedTargets([]);
    setIsMatchedTargetsRequestRun(false);
  };

  const handleClearForm = (
    change: <F extends keyof MessageForm>(name: F, value?: MessageForm[F] | undefined) => void
  ) => {
    change('email', undefined);
    change('firstName', undefined);
    change('lastName', undefined);
    change('phoneNumber', undefined);
    change('customFieldAnswers', {});
    handleClearAddress(change);
    setIsNewContact(true);
  };

  const handleMatchTargets = async (streetAddress: string, zipCode: string) => {
    const params = {
      streetAddress,
      zipCode,
    };

    setIsMatchedTargetsFetching(true);

    await request
      .get<Targets>(`/public/act/${uuid}/match_targets/`, { params })
      .then(({ data }) => {
        setMatchedTargets(data);
        setIsMatchedTargetsRequestRun(true);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        setMatchedTargets([]);

        throw data;
      })
      .finally(() => setIsMatchedTargetsFetching(false));
  };

  const handleChangePage = (page: number) => {
    setPaginationPage(page);
  };

  const handleFetchAddressSuggestions = async (
    inputValue: string, params: { selected?: string }
  ) => {
    setIsAutocompletePending(true);

    const addressSuggestion = !inputValue ?
      [] :
      await request
        .get<DataSuggestions>(
          `https://us-autocomplete-pro.api.smartystreets.com/lookup?key=${REACT_APP_SMARTY_STREETS_API_KEY}`,
          {
            params: {
              search: inputValue,
              ...params,
            },
          }
        )
        .then(({ data }) => data?.suggestions)
        .catch(({ response: { data = {} } = {} }) => {
          throw data;
        });

    setAddressSuggestions(addressSuggestion || []);
    setIsAutocompletePending(false);
  };

  const handleSendMessage = ({
    customFieldAnswers,
    isOptInEmail,
    isOptInText,
    message = [],
    personalizedMessage = '',
    streetAddress,
    subject,
    ...values
  }: MessageForm) => {
    setIsPending(true);
    const contact = {
      isOptInEmail,
      isOptInText,
      ...pick(values, [
        'prefix',
        'firstName',
        'lastName',
        'email',
        'phoneNumber',
        'zipCode',
      ]),
      ...(!isNewContact && contactInfo?.uuid ? { uuid: contactInfo.uuid } : {}),
      streetAddress: streetAddress?.streetLine ? `${streetAddress.streetLine}${streetAddress?.secondary ? ` ${streetAddress.secondary}` : ''}` : streetAddress,
    };
    const { emailType } = ActionCenterTypes;

    const params = {
      action: emailType,
      contact,
      ...(customFieldAnswers ?
        {
          customFields: Object.entries(customFieldAnswers).map(customField => ({
            uuid: customField[0],
            value: customField[1],
          })),
        } :
        {}),
      message: [
        ...message,
        ...(personalizedMessage ? [personalizedMessage] : []),
      ].join('\n\n'),
      messageId: prefilledMessage?.uuid,
      meta: {
        params: window.location.search,
        source: window.location.href,
        type: 'landing-v1',
      },
      subject,
    };

    return request
      .post(`/public/act/${uuid}/send_message/`, params)
      .then(({ data }) => {
        setMessageSent(true);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => data)
      .finally(() => setIsPending(false));
  };

  const paginationPages = Math.ceil(matchedTargets.length / 10);

  const renderTargets = () => {
    if (isMatchedTargetsFetching) {
      return (
        <CircularProgressWrapper>
          <CircularProgress />
        </CircularProgressWrapper>
      );
    }

    return (
      matchedTargets?.length ?
        matchedTargets
          .slice(paginationPage * 10, (paginationPage + 1) * 10)
          .map(target => (
            <Grid
              item
              key={target.uuid}
              xs={12}
              md={6}
            >
              <MatchTargetItem data-testid="target-list-item">
                <MatchTargetItemName>{`${target.firstName} ${target.lastName}`}</MatchTargetItemName>
                <MatchTargetItemPosition>{target.office}</MatchTargetItemPosition>
              </MatchTargetItem>
            </Grid>
          )) :
        (
          <NoData>
            No matched officials! Your address may be outside their
            district or they may not be included in this campaign.
          </NoData>
        )
    );
  };

  if (!REACT_APP_SMARTY_STREETS_API_KEY) {
    // eslint-disable-next-line no-console
    console.warn('Smarty Streets API key is missing!');
  }

  if (Object.keys(actionCenterDetails).length === 0) {
    return (
      <CircularProgressWrapper>
        <CircularProgress />
      </CircularProgressWrapper>
    );
  }

  const formInitialValues = () => {
    const customFieldAnswers = contactInfo?.customFields?.reduce(
      (convertedAcc, customFieldAnswer) => ({
        ...convertedAcc,
        [customFieldAnswer.customField]: customFieldAnswer.value,
      }),
      {}
    );

    return ({
      ...pick(contactInfo, [
        'email',
        'firstName',
        'lastName',
        'streetAddress',
        'zipCode',
      ]),
      customFieldAnswers,
      isOptInEmail: true,
      isOptInText: true,
      message: prefilledMessage?.body?.map(message => message.name) || [],
      phoneNumber: contactInfo ? handleNumberFormat(contactInfo.phoneNumber) : '',
      subject: prefilledMessage?.subject?.name,
    });
  };

  const renderMainMedia = () => {
    if (headlineConfig?.vimeoCode) {
      return (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: headlineConfig?.vimeoCode,
          }}
        />
      );
    }

    if (youtubeId) {
      return (
        <iframe
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          frameBorder="0"
          width="513"
          height="344"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title={youtubeId}
        />
      );
    }

    return (<MainImage src={landingPageImage} />);
  };

  return (
    <>
      <LandingPageWrapper item>
        <LogoWrapper>
          <Container>
            <Logo src={logo} />
          </Container>
        </LogoWrapper>
        <HeadlineSectionWrapper
          backgroundcolor={headlineConfig?.backgroundColor}
          messagesent={messageSent ? 1 : 0}
        >
          <StyledContainer
            container
            messagesent={messageSent ? 1 : 0}
          >
            <Grid
              item
              md
              xs={12}
            >
              <ActionCenterText textcolor={headlineConfig?.textColor}>
                {organization?.name}
              </ActionCenterText>
              <Headline textcolor={headlineConfig?.textColor}>
                {headlineConfig?.headlineText}
              </Headline>
              <HeadlineDescription textcolor={headlineConfig?.textColor}>
                {headlineConfig?.secondHeadlineText}
              </HeadlineDescription>
            </Grid>
            {(landingPageImage || youtubeId || headlineConfig?.vimeoCode) && (
              <Grid
                item
                md={6}
                xs={12}
              >
                <MainImageWrapper messagesent={messageSent ? 1 : 0}>
                  {renderMainMedia()}
                </MainImageWrapper>
              </Grid>
            )}
          </StyledContainer>
        </HeadlineSectionWrapper>
        {status === 'stopped' ?
          (<SectionTitleCenter>This Action Center has been stopped.</SectionTitleCenter>) :
          (
            <>
              <Form
                onSubmit={handleSendMessage}
                initialValues={formInitialValues()}
                keepDirtyOnReinitialize
                mutators={{
                  ...arrayMutators,
                }}
                render={({
                  dirtySinceLastSubmit,
                  handleSubmit,
                  submitting,
                  invalid,
                  values,
                  form,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Container>
                      <DescriptionSection messagesent={messageSent ? 1 : 0}>
                        <SectionTitle>{headlineConfig?.descriptionHeadlineText}</SectionTitle>
                        <StyledText
                          dangerouslySetInnerHTML={{
                            __html: replaceStringLineBreaksWithHtml(headlineConfig?.textArea),
                          }}
                        />
                      </DescriptionSection>
                      {!messageSent ?
                        (
                          <section>
                            <TakeActionTitleWrapper>
                              <SectionTitleCenter>Take action</SectionTitleCenter>
                              <StyledTextCenter>
                                Send an email to matching officials
                              </StyledTextCenter>
                            </TakeActionTitleWrapper>
                            <FormFieldWrapper>
                              <FormTitle>
                                From
                                {!!contactInfo && !isNewContact && (
                                <ClearFormButton onClick={() => {
                                  handleClearForm(form.change);
                                }}
                                >
                                  Not
                                  {' '}
                                  {contactInfo.firstName}
                                  {' '}
                                  {contactInfo.lastName}
                                  {' '}
                                  ? Reset Form
                                </ClearFormButton>
                                )}
                              </FormTitle>
                              <Grid
                                alignContent="space-between"
                                container
                              >
                                <StyledGrid
                                  item
                                  md={2}
                                  xs={12}
                                >
                                  <PrefixField prefixes={prefixes} />
                                </StyledGrid>
                                <StyledGrid
                                  item
                                  md={5}
                                  xs={12}
                                >
                                  <FirstNameField />
                                </StyledGrid>
                                <Grid
                                  item
                                  md={5}
                                  xs={12}
                                >
                                  <LastNameField />
                                </Grid>
                                <StyledGrid
                                  item
                                  md={6}
                                  xs={12}
                                >
                                  <PhoneNumberField />
                                </StyledGrid>
                                <Grid
                                  item
                                  xs={12}
                                  md={6}
                                >
                                  <EmailField />
                                </Grid>
                                {Object.keys(actionCenterDetails).length !== 0 && !isGeocoded && (
                                <>
                                  <Grid
                                    item
                                    md={6}
                                    xs={12}
                                  >
                                    <StreetAddressField
                                      addressSuggestions={addressSuggestions}
                                      isKnownContact={!!contactInfo?.streetAddress}
                                      isAutocompletePending={isAutocompletePending}
                                      change={form.change}
                                      handleFetchAddressSuggestions={handleFetchAddressSuggestions}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    md={6}
                                    xs={12}
                                  >
                                    <ZipCodeField />
                                  </Grid>
                                </>
                                )}
                                <CustomFields
                                  customFields={customFields}
                                  render={(customField: JSX.Element) => (
                                    <Grid
                                      item
                                      md={6}
                                      xs={12}
                                    >
                                      {customField}
                                    </Grid>
                                  )}
                                />
                              </Grid>
                            </FormFieldWrapper>
                            {!!isGeocoded && (
                            <MatchTargetsHeaderSection>
                              <SectionTitle textcolor={colors.white}>
                                Match Officials
                              </SectionTitle>
                              <Grid container>
                                <Grid
                                  item
                                  md={6}
                                  xs={12}
                                >
                                  <Field name="streetAddress">
                                    {({ input }) => (
                                      (
                                        contactInfo?.streetAddress ||
                                      !REACT_APP_SMARTY_STREETS_API_KEY
                                      ) ?
                                        (
                                          <StyledMatchTargetsTextField
                                            {...input}
                                            autoComplete="new-value"
                                            InputLabelProps={{
                                              style: {
                                                color: 'white',
                                              },
                                            }}
                                            InputProps={{
                                              style: {
                                                color: 'white',
                                              },
                                            }}
                                            label="Street Address"
                                          />
                                        ) :
                                        (
                                          <AddressAutocomplete
                                            {...input}
                                            autoComplete="new-value"
                                            addressSuggestions={addressSuggestions}
                                            color={colors.white}
                                            fetchAddressSuggestions={(inputValue, params) => (
                                              handleFetchAddressSuggestions(inputValue, params)
                                            )}
                                            isLoading={isAutocompletePending}
                                            setZipCode={newValue => form.change('zipCode', newValue)}
                                          />
                                        )
                                    )}
                                  </Field>
                                </Grid>
                                <Grid
                                  item
                                  md={3}
                                  xs={12}
                                >
                                  <Field
                                    name="zipCode"
                                    validate={required}
                                  >
                                    {({ input }) => (
                                      <StyledMatchTargetsTextField
                                        {...input}
                                        autoComplete="new-value"
                                        InputLabelProps={{
                                          style: {
                                            color: 'white',
                                          },
                                        }}
                                        inputProps={{
                                          maxLength: 5,
                                          style: {
                                            color: 'white',
                                          },
                                        }}
                                        id="zipcode"
                                        label="Zip Code"
                                      />
                                    )}
                                  </Field>
                                </Grid>
                                <Grid
                                  alignItems="flex-end"
                                  container
                                  item
                                  md={2}
                                  xs={6}
                                >
                                  <Popover
                                    shouldDisplayPopover={!values.streetAddress || !values.zipCode}
                                    text="Fill in Street Address and Zip Code fields"
                                  >
                                    <MatchTargetsButton
                                      disabled={
                                        isMatchedTargetsFetching ||
                                        !values.streetAddress ||
                                        !values.zipCode
                                      }
                                      onClick={() => {
                                        handleMatchTargets(values.streetAddress, values.zipCode);
                                      }}
                                    >
                                      Match Officials
                                    </MatchTargetsButton>
                                  </Popover>
                                </Grid>
                                <Grid
                                  alignItems="flex-end"
                                  container
                                  item
                                  justifyContent="flex-end"
                                  md={1}
                                  xs={6}
                                >
                                  <ClearButton onClick={() => handleClearAddress(form.change)}>
                                    Clear
                                  </ClearButton>
                                </Grid>
                              </Grid>
                            </MatchTargetsHeaderSection>
                            )}
                            {(
                              (isGeocoded && !submitting && isMatchedTargetsRequestRun) ||
                              (!isGeocoded && !!matchedTargets?.length)
                            ) && (
                            <>
                              <DividingLine />
                              <MatchTargetsSection>
                                <MatchTargetsResultsTitle>
                                  Results
                                  {' '}
                                  (
                                  {matchedTargets.length}
                                  )
                                </MatchTargetsResultsTitle>
                                <Grid
                                  container
                                  spacing={2}
                                >
                                  {renderTargets()}
                                </Grid>
                                <PaginationWrapper>
                                  {
                                times(paginationPages, i => (
                                  <Button
                                    onClick={() => handleChangePage(i)}
                                    type="button"
                                    key={i}
                                  >
                                    <PaginationSign
                                      isCurrentlySelected={paginationPage === i}
                                    />
                                  </Button>
                                ))
                              }
                                </PaginationWrapper>
                              </MatchTargetsSection>
                            </>
                            )}
                            <FormFieldWrapper>
                              <FormTitle>Subject of the email</FormTitle>
                              {prefilledMessage?.subject?.isAllowEdit ?
                                (
                                  <SubjectField />
                                ) :
                                (
                                  <MessageBlock>
                                    {prefilledMessage?.subject?.name}
                                  </MessageBlock>
                                )}
                            </FormFieldWrapper>
                            <FormFieldWrapper>
                              <FormTitle>Message</FormTitle>
                              <FieldArray name="message">
                                {() => (prefilledMessage?.body?.length ?
                                  (
                                    prefilledMessage.body.map((messageBlock, index) => (
                                      messageBlock.isAllowEdit ?
                                        (
                                          <MessageField
                                            index={index}
                                            key={messageBlock.name}
                                          />
                                        ) :
                                        (
                                          <MessageBlock key={messageBlock.name}>
                                            {messageBlock.name}
                                          </MessageBlock>
                                        )
                                    ))
                                  ) :
                                  null
                                )}
                              </FieldArray>
                              {
                                prefilledMessage?.isAllowPersonalMessage && (
                                  <PersonalizedMessageField />
                                )
                              }
                            </FormFieldWrapper>
                            <DisclaimerSection>
                              <Button
                                onClick={() => setShowEditPreferences(prevValue => !prevValue)}
                                variant="outlined"
                              >
                                {showEditPreferences ? 'Close Subscription Preferences' : 'Edit Subscription Preferences'}
                              </Button>
                              {showEditPreferences && (
                              <FormControl>
                                <IsOptEmailField />
                                <IsOptTextField />
                              </FormControl>
                              )}
                            </DisclaimerSection>
                            <SendMessageButtonWrapper>
                              <Popover
                                shouldDisplayPopover={
                                  (invalid && !dirtySinceLastSubmit) ||
                                  !matchedTargets.length
                                }
                                text={invalid ? 'Fill in all necessary fields' : 'Match officials first or update your address'}
                              >
                                <SendMessageButton
                                  buttoncolor={headlineConfig?.buttonColor}
                                  disabled={
                                    submitting ||
                                    (invalid && !dirtySinceLastSubmit) || !matchedTargets.length
                                  }
                                  type="submit"
                                >
                                  Send message
                                </SendMessageButton>
                              </Popover>
                            </SendMessageButtonWrapper>
                            <BrandingWrapper>
                              <span>Powered by Muster</span>
                              <LogoMark
                                alt="Muster logomark"
                                src="/muster_logomark_blue.png"
                              />
                            </BrandingWrapper>
                          </section>
                        ) :
                        (
                          <MessageSentSectionWrapper>
                            <StyledCheckCircleRoundedIcon />
                            <SectionTitleCenter>Message sent successfully</SectionTitleCenter>
                            <SubTitle>Thank you for taking action.</SubTitle>
                            {thankYouMessage && (
                              <div
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: thankYouMessage,
                                }}
                              />
                            )}
                            {isDonateButton && linkDonate && (
                            <DonateButtonWrapper>
                              <DonateButton
                                href={linkDonate}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <Button
                                  color="primary"
                                  variant="outlined"
                                >
                                  Donate
                                </Button>
                              </DonateButton>
                            </DonateButtonWrapper>
                            )}
                          </MessageSentSectionWrapper>
                        )
                }
                    </Container>
                  </form>
                )}
              />
              {
          socialShareButtonEnabled && (
            <Footer>
              <SubTitle>Want to do more? Share this page on your social media channels</SubTitle>
              <ShareIcons uuid={uuid} />
            </Footer>
          )
        }
            </>
          )}
      </LandingPageWrapper>
      <StyledBackdrop open={isPending}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>
    </>
  );
};

TakeActionLandingPageV1.propTypes = {
  actionCenterDetails: PropTypes.shape({
    customFields: PropTypes.arrayOf(PropTypes.shape({})),
    facebookPixelEnabled: PropTypes.bool,
    facebookPixelId: PropTypes.string,
    googleAnalyticsEnabled: PropTypes.bool,
    googleAnalyticsId: PropTypes.string,
    headlineConfig: PropTypes.shape({
      backgroundColor: PropTypes.string,
      buttonColor: PropTypes.string,
      descriptionHeadlineText: PropTypes.string,
      headlineText: PropTypes.string,
      secondHeadlineText: PropTypes.string,
      textArea: PropTypes.string,
      textColor: PropTypes.string,
      vimeoCode: PropTypes.string,
      youtubeId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]),
    }),
    isDonateButton: PropTypes.bool,
    isGeocoded: PropTypes.bool,
    landingPageImage: PropTypes.string,
    linkDonate: PropTypes.string,
    logo: PropTypes.string,
    messageBody: PropTypes.shape({
      isAllowPersonalMessage: PropTypes.bool,
      prefilledMessages: PropTypes.arrayOf(PropTypes.shape({
        isAllowEdit: PropTypes.bool,
        name: PropTypes.string,
      })),
    }),
    organization: PropTypes.shape({
      name: PropTypes.string,
    }),
    prefixes: PropTypes.arrayOf(PropTypes.string),
    socialShareButtonEnabled: PropTypes.bool,
    subjectBody: PropTypes.shape({
      isAllowEdit: PropTypes.bool,
      name: PropTypes.string,
    }),
    thankYouMessage: PropTypes.string,
    token: PropTypes.string,
  }).isRequired,
};
